import { useMemo, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import { Id } from '../../components/ui/Mono';
import RegistryManager from '../../components/warehouse/RegistryManager';
import { useActiveLocations } from '../../components/warehouse/lpnPickers';
import ProductionLotsExplorer from '../../components/warehouse/production/ProductionLotsExplorer';
import ProductionOrdersManager from '../../components/warehouse/production/ProductionOrdersManager';
import ProductionRecipeManager from '../../components/warehouse/production/ProductionRecipeManager';
import {
    CreateBom,
    CreateKitOption,
    CreateMachine,
    ListBoms,
    ListKitOptions,
    ListMachines,
    UpdateBom,
    UpdateKitOption,
    UpdateMachine,
} from '../../components/warehouse/production/productionApi';
import { useProductionLabels } from '../../components/warehouse/production/productionLabels';
import { FABRICA_EM_CONSTRUCAO } from '../../constants/featureFlags';

const TAB_ORDERS = 'production_orders';
const TAB_LOTS = 'production_lots';
const TAB_MACHINES = 'machines';
const TAB_BOMS = 'boms';
const TAB_KIT_OPTIONS = 'kit_options';

const TAB_IDS = [TAB_ORDERS, TAB_LOTS, TAB_MACHINES, TAB_BOMS, TAB_KIT_OPTIONS];

// Abas ainda não adaptadas à operação (a bancada de hoje não as usa): levam o
// selo "Em construção" e, com a chave ligada (padrão no melidev/prod), uma capa
// no lugar do conteúdo. Só visual; o código delas fica como está.
const TABS_EM_CONSTRUCAO = new Set([TAB_LOTS, TAB_BOMS, TAB_KIT_OPTIONS]);

const CapaEmConstrucao = ({ label }) => (
    <div className="rounded-2xl border-2 border-dashed border-warning-300 bg-warning-soft px-6 py-16 text-center dark:border-warning-800 dark:bg-warning-900/20">
        <Badge variant="warning">{label('wms.production.under_construction')}</Badge>
        <p className="mt-4 text-lg font-semibold text-warning-text dark:text-warning-200">
            {label('wms.production.under_construction_title')}
        </p>
        <p className="mt-2 text-sm text-warning-text dark:text-warning-300">
            {label('wms.production.under_construction_text')}
        </p>
    </div>
);

// Identidade estável entre renders (o RegistryManager refaz o GET quando a
// config muda de identidade — mesma disciplina da página de Configurações).
const MACHINE_LIST_KEYS = ['machines'];
const BOM_LIST_KEYS = ['boms'];
const KIT_OPTION_LIST_KEYS = ['kit_options'];

/**
 * /warehouse/production — CONSOLE DA FÁBRICA (Supervisor, desktop).
 *
 * Uma página, cinco abas, na ordem do trabalho: **Ordens de produção** (o que
 * está sendo feito), **Lotes e genealogia** (o que já saiu e de onde veio), e
 * os três cadastros que a fábrica precisa para funcionar — **Máquinas**,
 * **Ficha técnica** (o teórico contra o qual o refugo alerta) e **Opções de
 * kit** (as combinações que fecham um kit).
 *
 * Por que os cadastros não foram para a página de Configurações do Armazém: lá
 * cada aba tem a sua permissão granular (`wms:manage:operators`,
 * `:deposits`…), e a fábrica tem a DELA — `wms:manage:production`, que o
 * backend exige nas cinco rotas usadas aqui. Espalhar as abas de produção entre
 * duas páginas com regras de acesso diferentes faria o Supervisor de fábrica
 * ver meia fábrica em cada lugar.
 *
 * O APONTAMENTO não está aqui: ele é do chão, acontece na bancada do coletor
 * com o crachá (`wms:operate`). Console planeja; bancada aponta.
 */
const WarehouseProductionPage = () => {
    const { label } = useProductionLabels();
    const [activeTab, setActiveTab] = useState(TAB_ORDERS);
    const [focusedLot, setFocusedLot] = useState(null);

    // As posições de fábrica para o CASAMENTO da máquina: a entrada (FAB-*-IN)
    // que a alimenta e a saída (FAB-*-OT) onde nasce o pallet. Uma posição é de
    // uma máquina só (o backend recusa a repetida dizendo de qual).
    const locations = useActiveLocations(activeTab === TAB_MACHINES);
    const factoryOptions = useMemo(() => {
        const options = (type, noneKey) => [
            { value: '', label: label(noneKey) },
            ...locations.rows.filter((row) => row.type === type).map((row) => ({ value: row.code, label: row.code })),
        ];
        return {
            inputs: options('FACTORY_IN', 'wms.machine.input_none'),
            outputs: options('FACTORY_OUT', 'wms.machine.output_none'),
        };
    }, [locations.rows, label]);

    const machineConfig = useMemo(
        () => ({
            key: 'machines',
            entityLabel: label('wms.entity.machine'),
            entityLabelPlural: label('wms.production.machines_title'),
            countLabel: 'máquinas',
            description: label('wms.production.machines_description'),
            exportFileName: 'wms-maquinas',
            list: ListMachines,
            create: CreateMachine,
            update: UpdateMachine,
            listKeys: MACHINE_LIST_KEYS,
            conflictField: 'code',
            columns: [
                {
                    key: 'code',
                    label: label('wms.field.code'),
                    getValue: (row) => row.code,
                    render: (row) => <Id value={row.code} className="font-medium" />,
                },
                {
                    key: 'sector',
                    label: label('wms.field.sector'),
                    getValue: (row) => row.sector,
                    render: (row) => <Id value={row.sector} />,
                },
                {
                    key: 'input_location_code',
                    label: label('wms.machine.input'),
                    getValue: (row) => row.input_location_code ?? '',
                    render: (row) => (row.input_location_code ? <Id value={row.input_location_code} /> : '—'),
                },
                {
                    key: 'output_location_code',
                    label: label('wms.machine.output'),
                    getValue: (row) => row.output_location_code ?? '',
                    render: (row) => (row.output_location_code ? <Id value={row.output_location_code} /> : '—'),
                },
            ],
            fields: [
                {
                    name: 'code',
                    label: label('wms.field.code'),
                    type: 'text',
                    required: true,
                    mono: true,
                    uppercase: true,
                    placeholder: 'ex.: RBB-01',
                },
                {
                    // 3 letras: é o `[SETOR3]` do endereço `FAB-RBB-IN-P1` (§4).
                    // Quais letras existem é CADASTRO, não enum de schema.
                    name: 'sector',
                    label: label('wms.field.sector'),
                    type: 'text',
                    required: true,
                    mono: true,
                    uppercase: true,
                    maxLength: 3,
                    placeholder: 'ex.: RBB',
                    help: 'Três letras — as mesmas do endereço de fábrica (FAB-RBB-IN-P1 → RBB).',
                },
                {
                    name: 'input_location_code',
                    label: label('wms.machine.input'),
                    type: 'select',
                    options: factoryOptions.inputs,
                    defaultValue: '',
                },
                {
                    name: 'output_location_code',
                    label: label('wms.machine.output'),
                    type: 'select',
                    options: factoryOptions.outputs,
                    defaultValue: '',
                },
            ],
        }),
        [label, factoryOptions],
    );

    const bomConfig = useMemo(
        () => ({
            entityLabel: label('wms.entity.bom'),
            title: label('wms.production.boms_title'),
            description: label('wms.production.boms_description'),
            countLabel: label('wms.production.boms_count'),
            exportFileName: 'wms-fichas-tecnicas',
            list: ListBoms,
            create: CreateBom,
            update: UpdateBom,
            listKeys: BOM_LIST_KEYS,
            productFields: [
                {
                    name: 'product_in_id',
                    fieldLabel: label('wms.field.product_in'),
                    skuKey: 'product_in_sku',
                    nameKey: 'product_in_name',
                },
                {
                    name: 'product_out_id',
                    fieldLabel: label('wms.field.product_out'),
                    skuKey: 'product_out_sku',
                    nameKey: 'product_out_name',
                },
            ],
            numberFields: [
                {
                    name: 'theoretical_yield_pct',
                    fieldLabel: label('wms.field.theoretical_yield_pct'),
                    min: 0,
                    max: 100,
                    required: true,
                    suffix: '%',
                },
                {
                    name: 'theoretical_scrap_pct',
                    fieldLabel: label('wms.field.theoretical_scrap_pct'),
                    min: 0,
                    max: 100,
                    required: true,
                    suffix: '%',
                    help: 'Refugo apontado acima deste número dispara ALERTA no apontamento — nunca bloqueio.',
                },
            ],
        }),
        [label],
    );

    const kitOptionConfig = useMemo(
        () => ({
            entityLabel: label('wms.entity.kit_option'),
            title: label('wms.production.kit_options_title'),
            description: label('wms.production.kit_options_description'),
            countLabel: label('wms.production.kit_options_count'),
            exportFileName: 'wms-opcoes-kit',
            list: ListKitOptions,
            create: CreateKitOption,
            update: UpdateKitOption,
            listKeys: KIT_OPTION_LIST_KEYS,
            productFields: [
                {
                    name: 'product_kit_id',
                    fieldLabel: label('wms.production.kit_product'),
                    skuKey: 'kit_sku',
                    nameKey: 'kit_name',
                },
                {
                    name: 'component_product_id',
                    fieldLabel: label('wms.production.component_product'),
                    skuKey: 'component_sku',
                    nameKey: 'component_name',
                },
            ],
            numberFields: [
                {
                    name: 'qty_per_kit',
                    fieldLabel: label('wms.field.qty_per_kit'),
                    min: 0,
                    required: true,
                    help: 'Quantas PEÇAS deste componente formam um kit (6 pacotes, 3 pacotes…).',
                },
                {
                    // Não é gravado: o backend usa só para conferir a coerência
                    // (60 ÷ 6 = 10 é inteiro; 60 ÷ 7 não é). O tamanho do kit é
                    // da OP — duplicá-lo aqui criaria duas verdades.
                    name: 'kit_size',
                    fieldLabel: label('wms.field.kit_size'),
                    min: 1,
                    transient: true,
                    help: 'Opcional, só para conferir: o sistema recusa a combinação se o kit não for divisível pelas peças.',
                },
            ],
        }),
        [label],
    );

    const tabs = TAB_IDS.map((tabId) => ({ id: tabId, label: label(`wms.tab.${tabId}`) }));

    return (
        <div className="space-y-6">
            <PageHeader title={label('wms.production.title')} subtitle={label('wms.production.description')} />

            <div className="border-b border-mono-200 dark:border-mono-800">
                <nav className="flex space-x-8 overflow-x-auto">
                    {tabs.map((tab) => {
                        const isActive = tab.id === activeTab;
                        return (
                            <button
                                type="button"
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                aria-current={isActive ? 'true' : undefined}
                                className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                                    isActive
                                        ? 'border-black text-black dark:border-white dark:text-white'
                                        : 'border-transparent text-mono-500 hover:border-mono-300 hover:text-mono-700 dark:hover:text-mono-300'
                                }`}
                            >
                                {tab.label}
                                {TABS_EM_CONSTRUCAO.has(tab.id) ? (
                                    <Badge variant="warning" className="ml-2">
                                        {label('wms.production.under_construction')}
                                    </Badge>
                                ) : null}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {FABRICA_EM_CONSTRUCAO && TABS_EM_CONSTRUCAO.has(activeTab) ? (
                <CapaEmConstrucao label={label} />
            ) : activeTab === TAB_ORDERS ? (
                <ProductionOrdersManager
                    onOpenLot={(lot) => {
                        setFocusedLot(lot);
                        setActiveTab(TAB_LOTS);
                    }}
                />
            ) : activeTab === TAB_LOTS ? (
                <ProductionLotsExplorer initialLot={focusedLot} />
            ) : activeTab === TAB_MACHINES ? (
                <RegistryManager key={TAB_MACHINES} config={machineConfig} />
            ) : activeTab === TAB_BOMS ? (
                <ProductionRecipeManager key={TAB_BOMS} config={bomConfig} />
            ) : (
                <ProductionRecipeManager key={TAB_KIT_OPTIONS} config={kitOptionConfig} />
            )}
        </div>
    );
};

export default WarehouseProductionPage;
